<script setup lang="ts">
/**
 * Вложения — макет 49:1913: подпись «ВЛОЖЕНИЯ» и плитки 248×44 со скрепкой,
 * именем файла и строкой «PNG · 248 КБ · Анна».
 *
 * Файл кладут перетаскиванием на блок или кнопкой. Перетаскивание в макете не
 * нарисовано, но файл на страницу задачи чаще всего именно перетаскивают —
 * кнопка здесь запасной путь, а не единственный (docs/design-gaps.md).
 *
 * Плитка — ссылка на файл: она ведёт на временный адрес, который сервис
 * выписывает при чтении списка. Своей ручки скачивания у трекера нет и не надо.
 */
import { Paperclip, X } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { fileKind, formatFileSize } from '../attachments'
import { useIssueStore } from '../store'

import type { Attachment } from '../attachments'

const emit = defineEmits<{ failed: [error: unknown] }>()

const { t, locale } = useI18n()
const store = useIssueStore()

const field = ref<HTMLInputElement | null>(null)
const isOver = ref(false)
const isSending = ref(false)

const units = computed(() => ({
  kb: t('issue.attachments.kb'),
  mb: t('issue.attachments.mb'),
}))

/**
 * В макете под именем файла стоит одно имя человека, а не полное: «Анна», а не
 * «Анна Ли». Кто загрузил файл и вышел из проекта — тот же случай, что у
 * наблюдателей: имени взять неоткуда, и подпись обходится без него.
 */
const uploader = (attachment: Attachment): string => {
  const name = store.members.find((member) => member.id === attachment.uploadedBy)?.name ?? ''

  return name.split(' ')[0] ?? ''
}

const caption = (attachment: Attachment): string =>
  [
    fileKind(attachment.fileName, attachment.mimeType),
    formatFileSize(attachment.sizeBytes, units.value, locale.value),
    uploader(attachment),
  ]
    .filter(Boolean)
    .join(' · ')

const send = async (files: FileList | null) => {
  if (!files?.length) return

  isSending.value = true

  try {
    // По одному, а не пачкой: у сервиса ручка на один файл, а параллельная
    // отправка пяти вложений упирается в лимит запроса и падает целиком.
    for (const file of Array.from(files)) {
      await store.attachFile(file)
    }
  } catch (failure) {
    emit('failed', failure)
  } finally {
    isSending.value = false
  }
}

const onPick = async (event: Event) => {
  const input = event.target as HTMLInputElement

  await send(input.files)

  // Иначе второй выбор того же файла не поднимет событие: значение поля не
  // изменилось.
  input.value = ''
}

const onDrop = async (event: DragEvent) => {
  isOver.value = false

  await send(event.dataTransfer?.files ?? null)
}

const remove = async (attachment: Attachment) => {
  try {
    await store.removeAttachment(attachment.id)
  } catch (failure) {
    emit('failed', failure)
  }
}
</script>

<template>
  <section
    class="iatt"
    :class="{ 'iatt--over': isOver }"
    @dragover.prevent="isOver = true"
    @dragleave="isOver = false"
    @drop.prevent="onDrop"
  >
    <span v-if="store.attachments.length" class="iatt__label">
      {{ t('issue.attachments.title') }}
    </span>

    <div v-if="store.attachments.length" class="iatt__grid">
      <div v-for="attachment in store.attachments" :key="attachment.id" class="fchip">
        <a
          class="fchip__open"
          :href="attachment.url"
          target="_blank"
          rel="noopener"
          :title="attachment.fileName"
        >
          <Paperclip class="fchip__clip" :size="16" />
          <span class="fchip__text">
            <span class="fchip__name">{{ attachment.fileName }}</span>
            <span class="fchip__meta">{{ caption(attachment) }}</span>
          </span>
        </a>

        <button
          type="button"
          class="fchip__off"
          :aria-label="t('issue.attachments.remove', { name: attachment.fileName })"
          @click="remove(attachment)"
        >
          <X :size="12" />
        </button>
      </div>
    </div>

    <button type="button" class="iatt__add" :disabled="isSending" @click="field?.click()">
      <Paperclip :size="14" />
      {{ isSending ? t('issue.attachments.sending') : t('issue.attachments.add') }}
    </button>

    <input
      ref="field"
      class="iatt__field"
      type="file"
      multiple
      :aria-label="t('issue.attachments.add')"
      @change="onPick"
    />
  </section>
</template>

<style scoped>
.iatt {
  display: flex;
  flex-direction: column;
  /* 24 от подписи до плиток минус высота самой подписи */
  gap: 11px;
  border-radius: var(--radius-md);
}

/* Без файлов от блока остаётся одна кнопка — подпись над пустотой только
   занимает строку. */
.iatt:not(:has(.fchip)) {
  margin-top: -4px;
}

/* Пока файл держат над блоком, видно, куда он упадёт: рамка появляется
   отступом наружу, чтобы плитки не прыгали. */
.iatt--over {
  outline: 2px dashed var(--color-primary);
  outline-offset: 8px;
}

.iatt__label {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  line-height: 13px;
}

.iatt__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.fchip {
  position: relative;
  display: flex;
  align-items: center;
  width: 248px;
  height: 44px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

.fchip:hover {
  border-color: var(--color-border-strong);
}

.fchip__open {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0 14px 0 12px;
  color: inherit;
  text-decoration: none;
}

.fchip__clip {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.fchip__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.fchip__name {
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fchip__meta {
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  line-height: 13px;
}

/* Крестика в макете нет: удалять файл всё равно надо, и он появляется на
   наведении — как на связях задачи (docs/design-gaps.md). */
.fchip__off {
  position: absolute;
  top: 4px;
  right: 4px;
  display: none;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  cursor: pointer;
}

.fchip:hover .fchip__off,
.fchip__off:focus-visible {
  display: inline-flex;
}

.fchip__off:hover {
  color: var(--color-danger);
}

/* Кнопка тихая, как «добавить подзадачу»: в макете этого действия нет вовсе, а
   рамка на всю плитку перетягивала бы внимание с самих файлов. */
.iatt__add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.iatt__add:hover:not(:disabled) {
  color: var(--color-primary);
}

.iatt__add:disabled {
  cursor: progress;
}

.iatt__field {
  display: none;
}
</style>
